import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message, Spin } from "antd"; // Import Ant Design components

// Redux Actions
import { AcctokenD, RefftokenD, login } from "../store/actions";
import { getUserProfile } from "../store/reducers/userProfileReducer";
import { setExpiry } from "../store/reducers/tokenReducer";

// Components
import TwoFactorAuthModal from "../components/Auth/TwoFactorAuthModal";
import { baseUrl } from "../Axios";

const GoogleLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Ref to prevent double-execution in React Strict Mode
  const requestProcessed = useRef(false);

  /**
   * 1. Centralized logic to handle successful login and navigation
   * This keeps the main useEffect clean.
   */
  const handleLoginSuccess = async (idToken, refreshToken) => {
    try {
      // 1. Store Tokens
      dispatch(AcctokenD(idToken));
      dispatch(RefftokenD(refreshToken));

      // 2. Set Expiry (24 hours)
      const expiresInSeconds = 86400;
      const expiresAtDate = new Date();
      expiresAtDate.setSeconds(expiresAtDate.getSeconds() + expiresInSeconds);
      dispatch(setExpiry(expiresAtDate));

      // 3. Set Login State
      dispatch(login());

      // 4. Fetch Profile & Navigate
      message.loading({ content: "Loading user profile...", key: "login" });
      
      const userProfile = await dispatch(getUserProfile());
      
      message.success({ content: "Login successful!", key: "login" });

      // Navigation Logic based on account status
      if (Array.isArray(userProfile?.accounts) && userProfile.accounts.length > 0) {
        navigate("/AccountMetric");
      } else {
        // Fallback or specific route for new users
        navigate("/FundingEvaluationPayment/89"); 
      }

    } catch (error) {
      console.error("Profile Fetch Error:", error);
      message.error({ content: "Failed to load profile details.", key: "login" });
      navigate("/login");
    }
  };

  /**
   * 2. The Main Google Login Execution
   */
  const executeGoogleLogin = async (code) => {
    try {
      const response = await axios.get(
        `${baseUrl}/google/user/profile/?code=${code}`
      );

      const data = response.data;

      // CASE A: 2FA Required
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setShow2FAModal(true);
        setIsLoading(false); // Stop main loader, show modal
        return;
      }

      // CASE B: Direct Login Success
      await handleLoginSuccess(data.idToken, data.refreshToken);

    } catch (err) {
      console.error("Google Login Error:", err);
      
      // Extract specific error message from backend if available
      const errorMessage = err.response?.data?.message || err.response?.data?.detail || "Google Login Failed. Please try again.";
      
      message.error(errorMessage);
      navigate("/login");
    }
  };

  /**
   * 3. Effect to capture the code from URL
   */
  useEffect(() => {
    const code = searchParams.get("code");

    // Prevent running if no code, or if already processed (Strict Mode fix)
    if (!code) {
      message.error("No authorization code found.");
      navigate("/login");
      return;
    }

    if (requestProcessed.current) return;
    requestProcessed.current = true;

    executeGoogleLogin(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Handlers for 2FA Modal ---
  
  const handle2FAClose = () => {
    setShow2FAModal(false);
    setTempToken("");
    navigate("/login"); // User cancelled 2FA
  };

  const handle2FASuccess = () => {
    // Assuming the Modal handles the token dispatch internally upon success.
    // If the modal simply returns success, you might need to fetch profile here.
    setShow2FAModal(false);
    setTempToken("");
    
    // Optional: If the modal doesn't redirect, trigger profile fetch here:
    // handleLoginSuccess(newToken, newRefresh); 
  };

  return (
    <>
      {/* 2FA Modal Overlay */}
      {show2FAModal && (
        <TwoFactorAuthModal
          isOpen={show2FAModal}
          onClose={handle2FAClose}
          tempToken={tempToken}
          expiresInSeconds={86400}
          onSuccess={handle2FASuccess}
        />
      )}

      {/* Main Loading Screen */}
      {!show2FAModal && (
        <div style={styles.container}>
          <div style={styles.content}>
            <Spin size="large" tip="Verifying Google Account..." />
          </div>
        </div>
      )}
    </>
  );
};

// Simple styles object for cleaner JSX
const styles = {
  container: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#1f1f1f", // Assuming dark mode based on "color: white" in original
    color: "white",
  },
  content: {
    textAlign: "center",
  }
};

export default GoogleLogin;